import type { RegionSnapshot } from "@frontier/protocol/contracts";
import type { MapLandmark } from "../map-content";
import type { Point } from "./geometry";

interface Viewport {
  width: number;
  height: number;
}

export interface BackdropPicturePresentation extends Point {
  scale: number;
}

export interface BackdropLandmarkPresentation extends Point {
  visible: boolean;
  scale: number;
}

export function backdropPicturePresentation(
  viewport: Viewport,
  texture: Viewport,
  camera: Point,
  region: RegionSnapshot | undefined,
  zoom: number,
  offset: Point,
): BackdropPicturePresentation {
  const scale =
    Math.max(viewport.width / texture.width, viewport.height / texture.height) *
    1.18;
  const marginX = (texture.width * scale - viewport.width) / 2;
  const marginY = (texture.height * scale - viewport.height) / 2;
  const anchorX = region ? region.x + region.width / 2 : camera.x;
  const anchorY = region ? region.y + region.height / 2 : camera.y;
  return {
    scale,
    x:
      viewport.width / 2 +
      Math.max(
        -marginX,
        Math.min(
          marginX,
          (anchorX - camera.x) * 0.025 * zoom + offset.x * 0.35,
        ),
      ),
    y:
      viewport.height / 2 +
      Math.max(
        -marginY,
        Math.min(
          marginY,
          (anchorY - camera.y) * 0.025 * zoom + offset.y * 0.35,
        ),
      ),
  };
}

export function backdropLandmarkPresentation(
  landmark: MapLandmark,
  camera: Point,
  viewport: Viewport,
  zoom: number,
  region: RegionSnapshot | undefined,
  offset: Point,
): BackdropLandmarkPresentation {
  if (
    !region ||
    landmark.x < region.x ||
    landmark.x >= region.x + region.width ||
    landmark.y < region.y ||
    landmark.y >= region.y + region.height
  ) {
    return { x: 0, y: 0, scale: zoom, visible: false };
  }
  const anchorX = region.x + region.width / 2;
  const anchorY = region.y + region.height / 2;
  const x =
    viewport.width / 2 +
    (landmark.x - anchorX + (anchorX - camera.x) * landmark.parallax) * zoom +
    offset.x * (1 - landmark.parallax);
  const y =
    viewport.height / 2 +
    (landmark.y - anchorY + (anchorY - camera.y) * landmark.parallax) * zoom +
    offset.y * (1 - landmark.parallax);
  const radius = (landmark.size * zoom) / 2;
  return {
    x,
    y,
    scale: zoom,
    visible:
      x + radius >= 0 &&
      x - radius <= viewport.width &&
      y + radius >= 0 &&
      y - radius <= viewport.height,
  };
}

export function wrappedBackdropCoordinate(
  position: number,
  span: number,
): number {
  return ((position % span) + span) % span;
}
